/**
 * Integration connectors hook (INT-002/003/004/006, FND-009).
 *
 * Wraps the connector catalogue surface the sibling backend worker is adding:
 *   - GET /api/v1/integration/connectors → ConnectorStatus[]
 *
 * Tolerates `{ items }` / bare-array envelopes and degrades on 404 to a
 * deterministic seed covering the full TECH_SPEC §6 connector set (SFTP census,
 * OneSource, QuickBooks, RingCentral, Twilio, M365, FHIR, Epic) so the page
 * always renders the integration landscape.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';

/**
 * A connector and its catalogue status (FND-009, INT-002/003).
 *
 * Matches the backend `ConnectorStatus` (services/integration/schemas.py):
 * `name`, `kind` (category — eligibility/ehr/telephony/…), `status`, `last_run`,
 * `notes`. We synthesize a human `label` from the name for display.
 */
export interface ConnectorStatus {
  name: string;
  /** Vendor/system label, derived from the connector name. */
  label: string;
  /** Connector category, e.g. 'eligibility', 'ehr', 'telephony'. */
  kind: string;
  /** live | configured | planned. */
  status: 'live' | 'configured' | 'planned';
  last_run?: string | null;
  notes?: string;
}

/** Pretty-print a connector name for display. */
const LABELS: Record<string, string> = {
  sftp_census: 'SFTP census',
  onesource: 'OneSource eligibility',
  quickbooks: 'QuickBooks',
  ringcentral: 'RingCentral',
  twilio: 'Twilio',
  m365: 'Microsoft 365',
  fhir: 'FHIR',
  epic: 'Epic App Marketplace',
  epic_app_orchard: 'Epic App Orchard',
};

function labelFor(name: string): string {
  return LABELS[name] ?? name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function normalizeStatus(s: string | undefined): ConnectorStatus['status'] {
  const v = (s ?? '').toLowerCase();
  if (['live', 'active', 'enabled', 'running'].includes(v)) return 'live';
  if (['configured', 'ready', 'staged'].includes(v)) return 'configured';
  return 'planned';
}

const SEED_CONNECTORS: ConnectorStatus[] = [
  { name: 'sftp_census', label: 'SFTP census', kind: 'census', status: 'live', last_run: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(), notes: 'Daily inbound census drop (INT-001).' },
  { name: 'onesource', label: 'OneSource eligibility', kind: 'eligibility', status: 'live', last_run: new Date(Date.now() - 1000 * 60 * 32).toISOString(), notes: 'Approved Admissions eligibility checks (INT-005).' },
  { name: 'quickbooks', label: 'QuickBooks', kind: 'finance', status: 'configured', last_run: new Date(Date.now() - 1000 * 60 * 60 * 26).toISOString(), notes: 'Invoice + payment sync (INT-006).' },
  { name: 'ringcentral', label: 'RingCentral', kind: 'telephony', status: 'configured', last_run: null, notes: 'Click-to-dial + call transcription (INT-004).' },
  { name: 'twilio', label: 'Twilio', kind: 'messaging', status: 'live', last_run: new Date(Date.now() - 1000 * 60 * 12).toISOString(), notes: 'SMS notifications + patient invites (FND-008).' },
  { name: 'm365', label: 'Microsoft 365', kind: 'productivity', status: 'live', last_run: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(), notes: 'Email templates + staff calendar.' },
  { name: 'fhir', label: 'FHIR', kind: 'ehr', status: 'planned', last_run: null, notes: 'Clinical data exchange connector (INT-002).' },
  { name: 'epic', label: 'Epic App Marketplace', kind: 'ehr', status: 'planned', last_run: null, notes: 'EHR charge validation + write-back (INT-003).' },
];

interface RawConnector {
  name?: string;
  kind?: string;
  status?: string;
  last_run?: string | null;
  notes?: string;
}

interface RawConnectors {
  connectors?: RawConnector[];
  items?: RawConnector[];
}

function unwrap(data: RawConnectors | RawConnector[] | null | undefined): ConnectorStatus[] {
  const arr = Array.isArray(data) ? data : (data?.connectors ?? data?.items ?? []);
  if (!arr.length) return [];
  return arr.map((c, i) => {
    const name = c.name ?? `connector-${i}`;
    return {
      name,
      label: labelFor(name),
      kind: c.kind ?? '—',
      status: normalizeStatus(c.status),
      last_run: c.last_run ?? null,
      notes: c.notes,
    };
  });
}

interface UseConnectorsResult {
  connectors: ConnectorStatus[];
  available: boolean;
  isLoading: boolean;
  error: ApiError | null;
  refresh: () => void;
}

/** Loads the connector catalogue (FND-009), seeding the full set on 404. */
export function useConnectors(): UseConnectorsResult {
  const [connectors, setConnectors] = useState<ConnectorStatus[]>([]);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);
    apiClient
      .get<RawConnectors>('/integration/connectors')
      .then((data) => {
        const rows = unwrap(data);
        setConnectors(rows.length ? rows : SEED_CONNECTORS);
        setAvailable(rows.length > 0);
      })
      .catch((err: unknown) => {
        setConnectors(SEED_CONNECTORS);
        setAvailable(false);
        if (err instanceof ApiError && err.status !== 404) setError(err);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { connectors, available, isLoading, error, refresh };
}
